import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { VcuTelemetryService } from 'src/app/service/vcu-telemetry.service';
import { VcuTelemetryDataPoint, VcuTelemetryHistoryResponse, SseTelemetryEvent } from 'src/app/model/vcu-telemetry.model';
import { VcuService } from 'src/app/service/vcu.service';

interface IntervalOption {
  label: string;
  value: string;
}

interface ChannelInfo {
  key: string;
  label: string;
  enabled: boolean;
}

const CHART_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#34495e', '#e91e63', '#00bcd4',
  '#8bc34a', '#ff5722', '#607d8b', '#795548', '#673ab7'
];

@Component({
  selector: 'app-vcu-telemetry',
  templateUrl: './vcu-telemetry.component.html',
  styleUrl: './vcu-telemetry.component.css'
})
export class VcuTelemetryComponent implements OnInit, OnDestroy {
  vcuId: string = '';
  serialNumber: string = '';
  vcuNickname: string = '';

  startDate: Date = new Date();
  endDate: Date = new Date();

  intervalOptions: IntervalOption[] = [
    { label: 'Sem downsample', value: '250ms' },
    { label: '1 segundo', value: '1s' },
    { label: '5 segundos', value: '5s' },
    { label: '30 segundos', value: '30s' },
    { label: '1 minuto', value: '1m' },
    { label: '5 minutos', value: '5m' },
    { label: '15 minutos', value: '15m' },
    { label: '30 minutos', value: '30m' },
    { label: '1 hora', value: '1h' },
    { label: '6 horas', value: '6h' },
    { label: '1 dia', value: '1d' },
  ];
  selectedInterval: IntervalOption = this.intervalOptions[1];

  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  hasData: boolean = false;
  hasSearched: boolean = false;
  totalCount: number = 0;

  channels: ChannelInfo[] = [];

  chartData: any = {};
  chartOptions: any = {};

  private rawData: VcuTelemetryDataPoint[] = [];

  isLiveMode: boolean = false;
  liveStatus: 'idle' | 'connecting' | 'live' | 'offline' | 'stale' = 'idle';
  liveLastDataTime: string = '';

  get isConnecting(): boolean { return this.liveStatus === 'connecting'; }

  private liveSubscription: Subscription | null = null;
  private liveBuffer: VcuTelemetryDataPoint[] = [];
  private liveTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly LIVE_BUFFER_SIZE = 500;
  readonly LIVE_TIMEOUT_MS = 10_000;
  readonly LIVE_STALE_THRESHOLD_MS = 5 * 60 * 1_000;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private vcuTelemetryService: VcuTelemetryService,
    private vcuService: VcuService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    this.startDate = twentyFourHoursAgo;
    this.endDate = now;

    this.initChartOptions();

    this.activatedRoute.params.subscribe((params) => {
      this.vcuId = decodeURIComponent(params['id']);
      this.vcuService.setVcuID(this.vcuId);
      this.vcuService.getVcu().subscribe({
        next: (response) => {
          if (response && response.length > 0) {
            const vcu = VcuService.userVcuToVcu(response[0]);
            this.serialNumber = vcu.serialNumber;
            this.vcuNickname = vcu.surname;
          }
          this.buscarTelemetria();
        },
        error: () => {
          this.buscarTelemetria();
        }
      });
    });
  }

  buscarTelemetria(): void {
    if (!this.startDate || !this.endDate) return;

    this.isLoading = true;
    this.hasError = false;
    this.hasData = false;
    this.hasSearched = false;
    this.totalCount = 0;

    const startIso = this.toIsoString(this.startDate);
    const endIso = this.toIsoString(this.endDate);

    this.vcuTelemetryService
      .getTelemetryHistory(this.serialNumber, startIso, endIso, this.selectedInterval.value)
      .subscribe({
        next: (response: VcuTelemetryHistoryResponse) => {
          this.isLoading = false;
          this.rawData = response.data || [];
          this.totalCount = response.count ?? this.rawData.length;
          this.hasData = this.rawData.length > 0;
          this.hasSearched = true;
          this.detectChannels();
          this.buildChart();
        },
        error: (err) => {
          this.isLoading = false;
          this.hasError = true;
          this.hasSearched = true;
          this.errorMessage = 'Erro ao carregar dados de telemetria. Verifique o intervalo de datas e tente novamente.';
          console.error('Erro na telemetria:', err);
        }
      });
  }

  private detectChannels(): void {
    if (!this.rawData.length) {
      this.channels = [];
      return;
    }

    const allKeys = new Set<string>();
    this.rawData.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== 'timestamp') allKeys.add(key);
      });
    });

    const numericKeys = Array.from(allKeys).filter((key) =>
      this.rawData.some((point) => point[key] !== null && typeof point[key] === 'number')
    );

    const previousEnabled = new Map(this.channels.map((c) => [c.key, c.enabled]));

    this.channels = numericKeys.map((key) => ({
      key,
      label: this.formatChannelLabel(key),
      enabled: previousEnabled.has(key) ? previousEnabled.get(key)! : true
    }));
  }

  private formatChannelLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/\s+/g, ' ');
  }

  toggleChannel(channel: ChannelInfo): void {
    channel.enabled = !channel.enabled;
    this.buildChart();
  }

  private buildChart(): void {
    if (!this.rawData.length) return;

    const labels = this.rawData.map((p) => this.formatTimestamp(p['timestamp']));
    const enabledChannels = this.channels.filter((c) => c.enabled);

    const datasets = enabledChannels.map((channel, index) => ({
      label: channel.label,
      data: this.rawData.map((p) => (p[channel.key] !== null ? p[channel.key] : null)),
      borderColor: CHART_COLORS[index % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[index % CHART_COLORS.length] + '22',
      borderWidth: 2,
      pointRadius: this.rawData.length > 200 ? 0 : 2,
      pointHoverRadius: 4,
      fill: false,
      tension: 0.2,
      spanGaps: true
    }));

    this.chartData = { labels: [...labels], datasets: [...datasets] };
    this.cdr.detectChanges();
  }

  private initChartOptions(): void {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: '#1e2025',
          titleColor: '#e0e0e0',
          bodyColor: '#bbb',
          borderColor: '#3a3c42',
          borderWidth: 1,
          callbacks: {
            title: (items: any[]) => items[0]?.label ?? ''
          }
        }
      },
      scales: {
        x: {
          ticks: {
            maxTicksLimit: 12,
            maxRotation: 30,
            color: '#999'
          },
          grid: {
            color: '#3a3c42'
          },
          border: {
            color: '#3a3c42'
          }
        },
        y: {
          ticks: { color: '#999' },
          grid: { color: '#3a3c42' },
          border: { color: '#3a3c42' }
        }
      }
    };
  }

  private formatTimestamp(ts: string): string {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private toIsoString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}Z`;
  }

  toggleLive(): void {
    this.isLiveMode ? this.stopLive() : this.startLive();
  }

  startLive(): void {
    if (!this.serialNumber) return;
    this.isLiveMode = true;
    this.liveStatus = 'connecting';
    this.liveLastDataTime = '';
    this.liveBuffer = [];
    this.channels = [];
    this.chartData = {};
    this.hasSearched = false;

    this.liveTimeoutId = setTimeout(() => {
      if (this.liveStatus === 'connecting') {
        this.liveStatus = 'offline';
        this.cdr.detectChanges();
      }
    }, this.LIVE_TIMEOUT_MS);

    this.liveSubscription = this.vcuTelemetryService
      .getLiveStream(this.serialNumber)
      .subscribe({
        next: (event: SseTelemetryEvent) => {
          this.clearLiveTimeout();

          const points = this.normalizeSseSamples(event);
          if (points.length === 0) return;

          const diffMs = Date.now() - new Date(event.timestamp).getTime();
          if (diffMs > this.LIVE_STALE_THRESHOLD_MS) {
            this.liveStatus = 'stale';
            this.liveLastDataTime = this.formatTimestamp(event.timestamp);
          } else {
            this.liveStatus = 'live';
          }

          this.liveBuffer = [...this.liveBuffer, ...points].slice(-this.LIVE_BUFFER_SIZE);
          this.rawData = this.liveBuffer;
          this.hasData = true;
          this.hasSearched = true;
          this.detectChannels();
          this.buildChart();
        },
        error: () => {
          this.clearLiveTimeout();
          this.liveStatus = 'offline';
          this.cdr.detectChanges();
        }
      });
  }

  stopLive(): void {
    this.clearLiveTimeout();
    this.liveSubscription?.unsubscribe();
    this.liveSubscription = null;
    this.isLiveMode = false;
    this.liveStatus = 'idle';
  }

  private clearLiveTimeout(): void {
    if (this.liveTimeoutId !== null) {
      clearTimeout(this.liveTimeoutId);
      this.liveTimeoutId = null;
    }
  }

  private normalizeSseSamples(event: SseTelemetryEvent): VcuTelemetryDataPoint[] {
    return (event.data?.samples ?? []).map((s) => ({
      timestamp: s.timestamp,
      ...s.values
    }));
  }

  ngOnDestroy(): void {
    this.stopLive();
    this.clearLiveTimeout();
  }

  voltar(): void {
    this.router.navigate(['/vcu', this.vcuId]);
  }
}
